import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { hasSessionContextChanged, SESSION_CONTEXT_CHANGED_EVENT } from "../api/client";
import "./session-context-boundary.css";

export function SessionContextBoundary({ children }: { children: ReactNode }) {
  const [blocked, setBlocked] = useState(hasSessionContextChanged);
  const blocker = useRef<HTMLDialogElement>(null);
  const reloadButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const block = () => setBlocked(true);
    window.addEventListener(SESSION_CONTEXT_CHANGED_EVENT, block);
    if (hasSessionContextChanged()) block();
    return () => window.removeEventListener(SESSION_CONTEXT_CHANGED_EVENT, block);
  }, []);

  useEffect(() => {
    if (!blocked) return;
    const root = document.getElementById("root");
    const dialog = blocker.current;
    if (root) root.inert = true;
    // Native dialogs live above every CSS stacking context, including an open
    // payment or manager PIN dialog. Keep the recovery action in that top layer.
    if (dialog && !dialog.open) dialog.showModal();
    reloadButton.current?.focus();
    return () => {
      if (dialog?.open) dialog.close();
      if (root) root.inert = false;
    };
  }, [blocked]);

  // Keep the workspace mounted but inaccessible until an explicit reload adopts
  // the new session and rebuilds its branch, permissions and local UI.
  return <>{children}{blocked ? createPortal(
    <dialog ref={blocker} className="session-context-boundary" role="alertdialog" aria-modal="true"
      onCancel={event => event.preventDefault()}
      aria-labelledby="session-context-title" aria-describedby="session-context-description"
      data-testid="session-context-changed">
      <div className="session-context-boundary__card">
        <h1 id="session-context-title">تغيّرت جلسة العمل · Session changed</h1>
        <p id="session-context-description" dir="rtl">
          تغيّر المستخدم أو الفرع في نافذة أخرى. توقفت العمليات في هذه النافذة لحماية بيانات الفرع.
          أعد تحميل الصفحة لمتابعة الجلسة الحالية؛ ستُفقد التعديلات غير المحفوظة في النماذج المفتوحة.
        </p>
        <p lang="en" dir="ltr">The user or branch changed in another tab. Reload to continue with the current session.
          Unsaved form edits will be lost.</p>
        <button ref={reloadButton} type="button" onClick={() => window.location.reload()}
          data-testid="reload-session-context">إعادة تحميل الصفحة · Reload</button>
      </div>
    </dialog>, document.body
  ) : null}</>;
}
